import React, { useCallback, useEffect, useState } from 'react';
import { Paper, Button, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { BaseInfoCompActionsSet, BaseInfoCompProps, BaseInfoCompSaveAction, InfoCompPanelHeader, useModePanelStyle, useModeFormStyle, FontIcon }
    from '@moderepo/mode-app-components';
import { DeviceConfigParamSet } from '@moderepo/mode-internal-apis';
import { addDeviceConfigParamSetInputsInstance, normalizeDeviceConfigInputs, DeviceConfigInputErrors, DeviceConfigInputs,
    removeDeviceConfigParamSetInputsInstance, updateDeviceConfigInput } from './utils';
import { DeviceConfigForm, DeviceConfigFormProps } from '.';


const useStyle = makeStyles(() => {
    return {
        root: {
        },

        deviceConfigForm: {
            width: '100%',
        },
    };
}, {
    name: 'DeviceConfigComp', index: 1,
});



/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface DeviceConfigCompActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<DeviceConfigInputs>
}


/**
 * Interface for the DeviceConfigComp component's props. This component extends BaseInfoCompProps but it will omit the 'onFormInputChange'
 * callback from the base interface. The 'onFormInputChange' prop from the base interface only work for a flat inputs structure. The inputs
 * for the DeviceConfigComp has a more complicated structure so it will implement its own version of the 'onFormInputChange'
 */
export interface DeviceConfigCompProps extends Omit<BaseInfoCompProps, 'onFormInputChange'> {
    readonly deviceId: number;
    readonly fields: {};
    readonly deviceConfigSchema: readonly DeviceConfigParamSet[] | undefined;
    readonly deviceConfigInputs: DeviceConfigInputs;
    readonly inputErrors?: DeviceConfigInputErrors | undefined;

    readonly actions?: DeviceConfigCompActionsSet | undefined;
    readonly texts: DeviceConfigFormProps['texts'];

    readonly noDataMessageComp: React.ReactNode;

    /**
     * onFormInputChange handler. The handler will be provided with the inputName (the param key) and input value. The handler will also be
     * provided with the index of the paramSet which the param belonged to. If the paramSet is repeatable, the handler will also be
     * provided with the index of the repeat paramSet.
     */
    readonly onFormInputChange?: (inputName: string, value: any, paramSetIndex: number, paramSetInstanceIndex: number)=> void;
}



export const DeviceConfigComp: React.FC<DeviceConfigCompProps> = (props: DeviceConfigCompProps) => {

    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();

    const deviceConfigSchema = props.deviceConfigSchema;
    const onFormInputChange = props.onFormInputChange;


    // The state to keep track of all the user input for the deviceConfig. We will initialize the inputs with default values
    const [deviceConfigInputs, setDeviceConfigInputs] = useState<DeviceConfigInputs>(props.deviceConfigInputs);


    // When existing deviceConfig changed, we need to re-initialize the deviceConfigInputs with the new config
    useEffect(() => {
        setDeviceConfigInputs(props.deviceConfigInputs);
    }, [props.deviceConfigInputs]);


    /**
     * When one of the param input changed, we will update the deviceConfigInput state
     */
    const onDeviceInputChangeHandler = useCallback((inputName: string, value: any, paramSetIndex: number, paramSetInstanceIndex: number) => {
        // Update the current deviceConfigInputs
        const newConfig = updateDeviceConfigInput(inputName, value, paramSetIndex, paramSetInstanceIndex, deviceConfigInputs);
        setDeviceConfigInputs(newConfig);

        // Notify the handler the input changed
        if (onFormInputChange) {
            onFormInputChange(inputName, value, paramSetIndex, paramSetInstanceIndex);
        }
    }, [deviceConfigInputs, onFormInputChange]);


    /**
     * Create and add a new paramSet inputs instance for a paramSet
     */
    const onAddParamInstanceClicks = useCallback((paramSetIndex: number) => {
        const newDeviceConfigInputs = addDeviceConfigParamSetInputsInstance(props.deviceId, paramSetIndex, deviceConfigInputs, deviceConfigSchema);
        if (newDeviceConfigInputs) {
            setDeviceConfigInputs(newDeviceConfigInputs);

            // Manually trigger onClick event on the first param in the paramSetSchema so that the container can start editing mode
            const paramSetSchema = deviceConfigSchema?.[paramSetIndex];
            if (props.onFieldClicked && paramSetSchema && paramSetSchema.parameters.length > 0) {
                props.onFieldClicked(paramSetSchema.parameters[0].key);
            }
        }
    }, [deviceConfigInputs, deviceConfigSchema, props]);


    /**
     * Remove a paramSet inputs instance at the given paramSetIndex and paramSetInstanceIndex
     */
    const onRemoveParamInstanceClicks = useCallback((paramSetIndex: number, paramSetInstanceIndex: number) => {
        const newDeviceConfigInputs = removeDeviceConfigParamSetInputsInstance(paramSetIndex, paramSetInstanceIndex, deviceConfigInputs);
        if (newDeviceConfigInputs) {
            setDeviceConfigInputs(newDeviceConfigInputs);
        }
    }, [deviceConfigInputs]);


    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        setDeviceConfigInputs(props.deviceConfigInputs);
    }, [props.deviceConfigInputs]);


    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick(normalizeDeviceConfigInputs(deviceConfigInputs, deviceConfigSchema, true));
        }

        return false;
    }, [props.actions?.saveUpdate, deviceConfigSchema, deviceConfigInputs]);


    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className, classes.root)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                {deviceConfigSchema && deviceConfigSchema.length > 0 && (
                    <form
                        className={clsx(formClasses.root, props.formProps?.className)}
                        noValidate
                        autoComplete="on"
                        method="POST"
                        onSubmit={onFormSubmit}
                    >
                        <DeviceConfigForm
                            className={clsx(classes.deviceConfigForm)}
                            isEditing={props.isEditing}
                            deviceId={props.deviceId}
                            deviceConfigSchema={deviceConfigSchema}
                            deviceConfigInputs={deviceConfigInputs}
                            inputErrors={props.inputErrors}
                            texts={props.texts}
                            onFieldClicked={props.onFieldClicked}
                            onInputChange={onDeviceInputChangeHandler}
                            onAddParamInstanceClicks={onAddParamInstanceClicks}
                            onRemoveParamInstanceClicks={onRemoveParamInstanceClicks}
                        />

                        {props.isEditing && props.actions && (
                            <div className={clsx(formClasses.formActionContainer)}>
                                <div />
                                <div>
                                    {props.actions?.cancelEdit && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            size="small"
                                            startIcon={props.actions.cancelEdit.icon
                                                ? <FontIcon iconName={props.actions.cancelEdit.icon} />
                                                : undefined}
                                            onClick={() => {
                                                reverseFormInput();
                                                if (props.actions?.cancelEdit) {
                                                    props.actions.cancelEdit.onClick();
                                                }
                                            }}
                                        >
                                            {props.actions.cancelEdit.label}
                                        </Button>
                                    )}
                                    {props.actions?.saveUpdate && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            size="small"
                                            startIcon={props.actions.saveUpdate.icon
                                                ? <FontIcon iconName={props.actions.saveUpdate.icon} />
                                                : undefined}
                                        >
                                            {props.actions.saveUpdate.label}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </form>
                )}

                {(!deviceConfigSchema || deviceConfigSchema.length === 0) && (
                    props.noDataMessageComp
                )}
            </div>
        </Paper>
    );
};
